import { Category } from "@/types";

interface CategoryTab {
  id: number | string;
  label: string;
  color: string;
  icon?: React.ReactNode;
  slug?: string;
  order?: number;
  isActive?: boolean;
  newsCount?: number;
}

interface CategoryConfig {
  showAllTab: boolean;
  maxVisibleTabs: number;
  scrollable: boolean;
  defaultCategory: string;
  enableNewsCount: boolean;
  cacheTimeout: number; // minutes
}

interface CategoryResponse {
  status: boolean;
  message: string;
  data: {
    categories: Category[];
    config: CategoryConfig;
    meta: {
      total: number;
      active: number;
      cache_time: string;
    };
  };
}

/**
 * Unified Category Service
 * Provides dynamic, API-driven category management
 * Eliminates hardcoded category dependencies
 */
export class UnifiedCategoryService {
  private static readonly API_URL = "/api/v1/categories";
  private static readonly CACHE_KEY = "unified_categories_data";
  private static readonly DEFAULT_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

  private static cachedData: CategoryResponse["data"] | null = null;
  private static cacheTimestamp: number = 0;

  /**
   * Default category configuration
   */
  private static readonly DEFAULT_CONFIG: CategoryConfig = {
    showAllTab: true,
    maxVisibleTabs: 8,
    scrollable: true,
    defaultCategory: "home",
    enableNewsCount: false,
    cacheTimeout: 10,
  };

  /**
   * Fetch categories with configuration from API
   */
  static async fetchCategoriesWithConfig(
    forceRefresh: boolean = false,
  ): Promise<CategoryResponse["data"]> {
    // Check cache first
    if (!forceRefresh && this.isCacheValid()) {
      return this.cachedData!;
    }

    try {
      const response = await fetch(`${this.API_URL}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to fetch categories");
      }

      // Transform API v1 response to expected format
      const transformedData = {
        categories: result.data || [],
        config: {
          showAllTab: true,
          maxTabs: 10,
          enableNewsCount: false,
          sortBy: "order"
        }
      };

      // Cache the data
      this.cachedData = transformedData;
      this.cacheTimestamp = Date.now();

      return transformedData;
    } catch (error) {
      console.error("Error fetching categories:", error);

      // Return cached data if available, otherwise fallback
      if (this.cachedData) {
        return this.cachedData;
      }

      // Return minimal fallback
      return this.getFallbackData();
    }
  }

  /**
   * Get category tabs for navigation
   */
  static async getCategoryTabs(
    includeNewsCount: boolean = false,
    locationSlug?: string,
    homeTabLabel: string = "Beranda",
  ): Promise<CategoryTab[]> {
    try {
      const data = await this.fetchCategoriesWithConfig();
      const { categories, config } = data;

      const tabs: CategoryTab[] = [];

      // Add "All" tab if configured
      if (config.showAllTab) {
        tabs.push({
          id: "home",
          label: homeTabLabel,
          color: "bg-gray-600",
          slug: undefined,
          order: 0,
          isActive: true,
          newsCount: includeNewsCount
            ? await this.getTotalNewsCount(locationSlug)
            : undefined,
        });
      }

      // Convert categories to tabs
      const categoryTabs: CategoryTab[] = categories
        .filter((cat) => cat.is_active !== false) // Filter active categories
        .map((cat) => ({
          id: cat.id,
          label: cat.name,
          color: this.getCategoryColor(cat.name),
          slug: cat.slug,
          order: cat.order || 999,
          isActive: true,
          newsCount: includeNewsCount ? cat.news_count : undefined,
        }))
        .sort((a, b) => {
          // Sort by order field, then by name
          const orderA = a.order || 999;
          const orderB = b.order || 999;

          if (orderA !== orderB) {
            return orderA - orderB;
          }

          return a.label.localeCompare(b.label);
        });

      tabs.push(...categoryTabs);

      // Limit visible tabs if configured
      if (config.maxVisibleTabs > 0) {
        return tabs.slice(0, config.maxVisibleTabs);
      }

      return tabs;
    } catch (error) {
      console.error("Error getting category tabs:", error);
      return this.getFallbackTabs(homeTabLabel);
    }
  }

  /**
   * Get category configuration
   */
  static async getCategoryConfig(): Promise<CategoryConfig> {
    try {
      const data = await this.fetchCategoriesWithConfig();
      return { ...this.DEFAULT_CONFIG, ...data.config };
    } catch (error) {
      console.error("Error getting category config:", error);
      return this.DEFAULT_CONFIG;
    }
  }

  /**
   * Find category by slug
   */
  static async findCategoryBySlug(slug: string): Promise<Category | null> {
    try {
      const data = await this.fetchCategoriesWithConfig();
      const category = data.categories.find((cat) => cat.slug === slug);
      return category || null;
    } catch (error) {
      console.error("Error finding category by slug:", error);
      return null;
    }
  }

  /**
   * Get active categories only
   */
  static async getActiveCategories(): Promise<Category[]> {
    try {
      const data = await this.fetchCategoriesWithConfig();
      return data.categories.filter((cat) => cat.is_active !== false);
    } catch (error) {
      console.error("Error getting active categories:", error);
      return [];
    }
  }

  /**
   * Check if category exists and is active
   */
  static async isCategoryActive(slug: string): Promise<boolean> {
    try {
      const category = await this.findCategoryBySlug(slug);
      return category !== null && category.is_active !== false;
    } catch (error) {
      console.error("Error checking category status:", error);
      return false;
    }
  }

  /**
   * Get category display name by slug
   */
  static async getCategoryDisplayName(slug: string): Promise<string> {
    try {
      const category = await this.findCategoryBySlug(slug);
      return category?.name || slug;
    } catch (error) {
      console.error("Error getting category display name:", error);
      return slug;
    }
  }

  /**
   * Get total news count across all categories
   */
  private static async getTotalNewsCount(
    locationSlug?: string,
  ): Promise<number> {
    try {
      const params = new URLSearchParams();
      if (locationSlug) params.set("location", locationSlug);

      const response = await fetch(
        `${this.API_URL}/total-news-count?${params}`,
      );
      const result = await response.json();

      return result.data?.total || 0;
    } catch (error) {
      console.error("Error getting total news count:", error);
      return 0;
    }
  }

  /**
   * Get category color based on name
   */
  private static getCategoryColor(categoryName: string): string {
    const colorMap: { [key: string]: string } = {
      Politik: "bg-red-600",
      Ekonomi: "bg-green-600",
      Teknologi: "bg-blue-600",
      Olahraga: "bg-orange-600",
      Hiburan: "bg-purple-600",
      Kesehatan: "bg-teal-600",
      Pendidikan: "bg-indigo-600",
      Lifestyle: "bg-pink-600",
      Selebriti: "bg-rose-600",
      Celebrity: "bg-rose-600",
      Viral: "bg-yellow-600",
      Kriminal: "bg-gray-700",
      Internasional: "bg-cyan-600",
    };

    // Find matching color by name or partial match
    const exactMatch = colorMap[categoryName];
    if (exactMatch) return exactMatch;

    // Try partial matches
    const lowerCaseName = categoryName.toLowerCase();
    for (const [key, color] of Object.entries(colorMap)) {
      if (
        lowerCaseName.includes(key.toLowerCase()) ||
        key.toLowerCase().includes(lowerCaseName)
      ) {
        return color;
      }
    }

    // Default color
    return "bg-gray-600";
  }

  /**
   * Check if cached data is still valid
   */
  private static isCacheValid(): boolean {
    if (!this.cachedData || !this.cacheTimestamp) return false;

    const cacheAge = Date.now() - this.cacheTimestamp;
    const maxAge = this.cachedData.config?.cacheTimeout
      ? this.cachedData.config.cacheTimeout * 60 * 1000
      : this.DEFAULT_CACHE_TTL;

    return cacheAge < maxAge;
  }

  /**
   * Get fallback data when API fails
   */
  private static getFallbackData(): CategoryResponse["data"] {
    return {
      categories: [
        { id: 1, name: "Politik", slug: "politik", order: 1, is_active: true },
        { id: 2, name: "Ekonomi", slug: "ekonomi", order: 2, is_active: true },
        {
          id: 3,
          name: "Olahraga",
          slug: "olahraga",
          order: 3,
          is_active: true,
        },
        {
          id: 4,
          name: "Teknologi",
          slug: "teknologi",
          order: 4,
          is_active: true,
        },
        { id: 5, name: "Hiburan", slug: "hiburan", order: 5, is_active: true },
      ] as Category[],
      config: this.DEFAULT_CONFIG,
      meta: {
        total: 5,
        active: 5,
        cache_time: new Date().toISOString(),
      },
    };
  }

  /**
   * Get fallback tabs when API fails
   */
  private static getFallbackTabs(
    homeTabLabel: string = "Beranda",
  ): CategoryTab[] {
    return [
      {
        id: "home",
        label: homeTabLabel,
        color: "bg-gray-600",
        slug: undefined,
        order: 0,
        isActive: true,
      },
      {
        id: 1,
        label: "Politik",
        color: "bg-red-600",
        slug: "politik",
        order: 1,
        isActive: true,
      },
      {
        id: 2,
        label: "Ekonomi",
        color: "bg-green-600",
        slug: "ekonomi",
        order: 2,
        isActive: true,
      },
      {
        id: 3,
        label: "Olahraga",
        color: "bg-orange-600",
        slug: "olahraga",
        order: 3,
        isActive: true,
      },
      {
        id: 4,
        label: "Teknologi",
        color: "bg-blue-600",
        slug: "teknologi",
        order: 4,
        isActive: true,
      },
    ];
  }

  /**
   * Clear cache manually
   */
  static clearCache(): void {
    this.cachedData = null;
    this.cacheTimestamp = 0;

    // Also clear localStorage cache if exists
    if (typeof window !== "undefined") {
      localStorage.removeItem(this.CACHE_KEY);
    }
  }

  /**
   * Refresh categories (clear cache and fetch fresh data)
   */
  static async refreshCategories(): Promise<CategoryResponse["data"]> {
    this.clearCache();
    return this.fetchCategoriesWithConfig(true);
  }

  /**
   * Get cache information for debugging
   */
  static getCacheInfo(): {
    isCached: boolean;
    cacheAge: number;
    categoriesCount: number;
    lastUpdate: string;
  } {
    return {
      isCached: this.cachedData !== null,
      cacheAge: this.cacheTimestamp ? Date.now() - this.cacheTimestamp : 0,
      categoriesCount: this.cachedData?.categories?.length || 0,
      lastUpdate: this.cacheTimestamp
        ? new Date(this.cacheTimestamp).toISOString()
        : "Never",
    };
  }

  /**
   * Validate category configuration
   */
  static validateConfig(config: Partial<CategoryConfig>): CategoryConfig {
    return {
      showAllTab: config.showAllTab ?? this.DEFAULT_CONFIG.showAllTab,
      maxVisibleTabs: Math.max(
        0,
        config.maxVisibleTabs ?? this.DEFAULT_CONFIG.maxVisibleTabs,
      ),
      scrollable: config.scrollable ?? this.DEFAULT_CONFIG.scrollable,
      defaultCategory:
        config.defaultCategory || this.DEFAULT_CONFIG.defaultCategory,
      enableNewsCount:
        config.enableNewsCount ?? this.DEFAULT_CONFIG.enableNewsCount,
      cacheTimeout: Math.max(
        1,
        config.cacheTimeout ?? this.DEFAULT_CONFIG.cacheTimeout,
      ),
    };
  }

  /**
   * Export categories for external systems (RSS, sitemap, etc.)
   */
  static async exportCategories(
    format: "json" | "csv" = "json",
  ): Promise<string> {
    try {
      const categories = await this.getActiveCategories();

      if (format === "csv") {
        const csvHeader = "ID,Name,Slug,Order,Active\n";
        const csvRows = categories
          .map(
            (cat) =>
              `${cat.id},"${cat.name}","${cat.slug}",${cat.order || 999},${cat.is_active ? "true" : "false"}`,
          )
          .join("\n");

        return csvHeader + csvRows;
      }

      return JSON.stringify(categories, null, 2);
    } catch (error) {
      console.error("Error exporting categories:", error);
      return format === "json" ? "[]" : "ID,Name,Slug,Order,Active\n";
    }
  }
}

// Export types for external use
export type { CategoryTab, CategoryConfig };

// Export default
export default UnifiedCategoryService;
